import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Mail, Lock, User, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { supabase } from "@/integrations/supabase/client";
import { lovable } from "@/integrations/lovable";
import { SiteHeader } from "@/components/SiteHeader";
import { SiteFooter } from "@/components/SiteFooter";
import { SITE } from "@/config/site";

export const Route = createFileRoute("/auth")({
  head: () => ({
    meta: [
      { title: "Sign in or Register · Surer Adda With Sanchayeeta" },
      { name: "description", content: "Sign in to book Sanchayeeta Seal for your event or manage your bookings." },
      { property: "og:title", content: "Sign in · Surer Adda" },
      { property: "og:description", content: "Access your account to book performances or track existing enquiries." },
      { property: "og:url", content: `${SITE.url}/auth` },
      { name: "robots", content: "noindex" },
    ],
    links: [{ rel: "canonical", href: `${SITE.url}/auth` }],
  }),
  component: AuthPage,
});

const loginSchema = z.object({
  email: z.string().email().max(255),
  password: z.string().min(6).max(72),
});
const signupSchema = loginSchema.extend({
  full_name: z.string().trim().min(2).max(100),
  phone: z.string().regex(/^[6-9]\d{9}$/, "Enter 10-digit mobile"),
  confirm_password: z.string().min(6).max(72),
}).refine((d) => d.password === d.confirm_password, {
  path: ["confirm_password"],
  message: "Passwords do not match",
});

function AuthPage() {
  const navigate = useNavigate();

  // already signed in? bounce home — and react to sign-in events from the OAuth popup
  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) navigate({ to: "/", replace: true });
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if ((event === "SIGNED_IN" || event === "INITIAL_SESSION") && session?.user) {
        navigate({ to: "/", replace: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleGoogle = async () => {
    console.log("[auth] Google click → calling lovable broker");
    try {
      const res = await lovable.auth.signInWithOAuth("google", { redirect_uri: window.location.origin });
      console.log("[auth] broker result", { redirected: (res as any)?.redirected, error: (res as any)?.error?.message });
      if (res.error) return toast.error(res.error.message);
      if (!res.redirected) {
        const { data } = await supabase.auth.getSession();
        console.log("[auth] post-broker session?", !!data.session?.user);
        navigate({ to: "/", replace: true });
      }
    } catch (e: any) {
      console.error("[auth] Google sign-in threw", e);
      toast.error(e?.message ?? "Google sign-in failed");
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto flex max-w-md flex-col items-center px-4 py-16">
        <p className="font-script text-4xl text-primary">Surer Adda</p>
        <h1 className="mt-2 text-2xl">Welcome</h1>
        <p className="mt-1 text-sm text-muted-foreground">Sign in or create an account to book.</p>

        <div className="mt-8 w-full rounded-2xl border border-border bg-card p-6 shadow-sm">
          <Tabs defaultValue="login">
            <TabsList className="grid grid-cols-2">
              <TabsTrigger value="login">Sign in</TabsTrigger>
              <TabsTrigger value="signup">Register</TabsTrigger>
            </TabsList>
            <TabsContent value="login" className="mt-6"><LoginForm onSuccess={() => navigate({ to: "/", replace: true })} /></TabsContent>
            <TabsContent value="signup" className="mt-6"><SignupForm /></TabsContent>
          </Tabs>

          <div className="my-6 flex items-center gap-3 text-xs uppercase tracking-wider text-muted-foreground">
            <div className="h-px flex-1 bg-border" /> or <div className="h-px flex-1 bg-border" />
          </div>
          <Button onClick={handleGoogle} variant="outline" className="w-full">
            <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z"/><path fill="#FBBC05" d="M5.84 14.09a6.6 6.6 0 0 1 0-4.18V7.07H2.18a11 11 0 0 0 0 9.86l3.66-2.84z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z"/></svg>
            Continue with Google
          </Button>
          <p className="mt-4 text-center text-xs text-muted-foreground">
            <Link to="/reset-password" className="hover:text-primary">Forgot password?</Link>
          </p>
        </div>
      </div>
      <SiteFooter />
    </div>
  );
}

function LoginForm({ onSuccess }: { onSuccess: () => void }) {
  const form = useForm<z.infer<typeof loginSchema>>({ resolver: zodResolver(loginSchema), defaultValues: { email: "", password: "" } });
  const onSubmit = async (d: z.infer<typeof loginSchema>) => {
    const { error } = await supabase.auth.signInWithPassword(d);
    if (error) return toast.error(error.message);
    toast.success("Signed in!");
    onSuccess();
  };
  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
      <div>
        <Label htmlFor="login-email" className="text-xs">Email</Label>
        <div className="relative"><Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input id="login-email" type="email" autoComplete="email" className="pl-9" {...form.register("email")} /></div>
        {form.formState.errors.email && <p className="mt-1 text-xs text-destructive">{form.formState.errors.email.message}</p>}
      </div>
      <div>
        <Label htmlFor="login-password" className="text-xs">Password</Label>
        <div className="relative"><Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input id="login-password" type="password" autoComplete="current-password" className="pl-9" {...form.register("password")} /></div>
      </div>
      <Button type="submit" disabled={form.formState.isSubmitting} className="w-full bg-primary text-primary-foreground">
        {form.formState.isSubmitting ? "Signing in…" : "Sign in"}
      </Button>
    </form>
  );
}

function SignupForm() {
  const navigate = useNavigate();
  const form = useForm<z.infer<typeof signupSchema>>({ resolver: zodResolver(signupSchema), defaultValues: { full_name: "", phone: "", email: "", password: "", confirm_password: "" } });
  const onSubmit = async (d: z.infer<typeof signupSchema>) => {
    const { trackEvent } = await import("@/lib/analytics");
    const { data, error } = await supabase.auth.signUp({
      email: d.email,
      password: d.password,
      options: {
        emailRedirectTo: `${window.location.origin}/`,
        data: { full_name: d.full_name, phone: d.phone },
      },
    });
    if (error) {
      trackEvent("sign_up_failed", { method: "email", reason: error.message });
      return toast.error(error.message);
    }
    const params = new URLSearchParams(window.location.search);
    trackEvent("sign_up", {
      method: "email",
      user_id: data.user?.id,
      source: params.get("from") ?? "auth_page",
    });
    toast.success("Account created! You're signed in.");
    navigate({ to: "/", replace: true });
  };
  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-3">
      <div>
        <Label htmlFor="signup-name" className="text-xs">Full name</Label>
        <div className="relative"><User className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input id="signup-name" autoComplete="name" className="pl-9" {...form.register("full_name")} /></div>
      </div>
      <div>
        <Label htmlFor="signup-phone" className="text-xs">Mobile</Label>
        <div className="relative"><Phone className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input id="signup-phone" autoComplete="tel" className="pl-9" maxLength={10} {...form.register("phone")} /></div>
      </div>
      <div>
        <Label htmlFor="signup-email" className="text-xs">Email</Label>
        <div className="relative"><Mail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input id="signup-email" type="email" autoComplete="email" className="pl-9" {...form.register("email")} /></div>
        {form.formState.errors.email && <p className="mt-1 text-xs text-destructive">{form.formState.errors.email.message}</p>}
      </div>
      <div>
        <Label htmlFor="signup-password" className="text-xs">Password (min 6 chars)</Label>
        <div className="relative"><Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input id="signup-password" type="password" autoComplete="new-password" className="pl-9" {...form.register("password")} /></div>
        {form.formState.errors.password && <p className="mt-1 text-xs text-destructive">{form.formState.errors.password.message}</p>}
      </div>
      <div>
        <Label htmlFor="signup-confirm" className="text-xs">Confirm password</Label>
        <div className="relative"><Lock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" /><Input id="signup-confirm" type="password" autoComplete="new-password" className="pl-9" {...form.register("confirm_password")} /></div>
        {form.formState.errors.confirm_password && <p className="mt-1 text-xs text-destructive">{form.formState.errors.confirm_password.message}</p>}
      </div>
      <Button type="submit" disabled={form.formState.isSubmitting} className="w-full bg-primary text-primary-foreground">
        {form.formState.isSubmitting ? "Creating…" : "Create account"}
      </Button>
    </form>
  );
}
